import React, { useState, useMemo } from 'react';
import { Box, Typography, CircularProgress } from '@material-ui/core';
import { DateTime } from 'luxon';
import { gql } from '@apollo/client';
import { useCampfireQuery } from '../../../../global/network/useCampfireQuery';
import { useCampfireFetch } from '../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../global/config/useApiUrl';
import { useUser } from '../../../../global/auth/useUser';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { MyActivityStatusIndicator } from '../../vol-dashboard/vol-dashboard-my-elements/main-section/my-activities/MyActivityStatusIndicator';
import { ActivityAvailability } from '../../../../common/availability-center/ActivityAvailability/ActivityAvailability';
import { getRosterActions } from '../../activities-v2/helpers';
import { RosteredStatus } from '../../activities-v2/ActivityTypes';

const GET_ROSTER_CARD_DATA = gql`
  query GetChatRosterCardData($activityId: String!, $activityDate: Date!, $volunteerId: String!) {
    vm {
      activity(activityId: $activityId) {
        activityId
        name
        startTime
        endTime
        hasOpenRoster
        activityLocation {
          placesAddress {
            description
          }
        }
        activityRemoteLocation {
          activityRemoteLocationId
        }
        program {
          name
        }
        sessions(includeOneOff: true) {
          sessionId
          name
        }
        publishedRoster(activityDate: $activityDate) {
          rosterId
          datePublished
          name
          rosterings {
            publishedRosteringId
            volunteer {
              volunteerId
            }
            sessionRosterings {
              session {
                sessionId
              }
            }
            dateRemoved
          }
        }
      }
      volunteer(volunteerId: $volunteerId) {
        volunteerId
        activityEnrolments {
          activityEnrolmentId
          activity {
            activityId
          }
          availabilities {
            activityAvailabilityId
            activityDate
            sessionAvailabilities {
              session {
                sessionId
              }
            }
          }
          unavailabilities {
            activityUnavailabilityId
            activityDate
          }
        }
      }
    }
  }
`;

interface ChatRosterCardProps {
  activityId: string;
  activityDate: string;
}

export const ChatRosterCard = ({ activityId, activityDate }: ChatRosterCardProps) => {
  const { getVolunteerIdentity } = useUser();
  const { volunteerId } = getVolunteerIdentity();
  const apiUrl = useApiUrl();
  const { theme } = useCampfireTheme();

  const { data, loading, refetch } = useCampfireQuery<any, any>(GET_ROSTER_CARD_DATA, {
    options: { variables: { activityId, activityDate, volunteerId } },
  });

  const availabilityFetch = useCampfireFetch<any>({ defer: true });
  const unavailabilityFetch = useCampfireFetch<any>({ defer: true });
  const [sessionSelectOpen, setSessionSelectOpen] = useState(false);

  const activity = data?.vm?.activity;
  const volunteer = data?.vm?.volunteer;

  const rosterData = useMemo(() => {
    if (!activity || !volunteer) {
      return { status: 'not on team' as RosteredStatus, enrolmentId: null, hasOpenRoster: false };
    }

    const enrolment = volunteer.activityEnrolments?.find((e: any) => e.activity.activityId === activityId);

    if (!enrolment) {
      return { status: 'not on team' as RosteredStatus, enrolmentId: null, hasOpenRoster: activity.hasOpenRoster };
    }

    const myRostering = activity.publishedRoster?.rosterings?.find(
      (r: any) => r.volunteer.volunteerId === volunteerId && !r.dateRemoved
    );
    const isRostered = !!myRostering;
    const hasAvailability = enrolment.availabilities?.some((a: any) => a.activityDate === activityDate);
    const hasUnavailability = enrolment.unavailabilities?.some((u: any) => u.activityDate === activityDate);

    let status: RosteredStatus = 'unsure';
    if (isRostered) status = 'rostered';
    else if (hasAvailability) status = 'available';
    else if (hasUnavailability) status = 'unavailable';

    return {
      status,
      enrolmentId: enrolment.activityEnrolmentId,
      hasOpenRoster: activity.hasOpenRoster,
      publishedRosteringId: myRostering?.publishedRosteringId || null,
    };
  }, [activity, volunteer, activityId, activityDate, volunteerId]);

  const { status, enrolmentId, hasOpenRoster, publishedRosteringId } = rosterData;

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!activity) {
    return (
      <Box style={{ padding: 12, border: '1px solid #efefef', borderRadius: 8 }}>
        <Typography variant='body2' style={{ color: '#999' }}>
          Activity no longer available
        </Typography>
      </Box>
    );
  }

  const dt = DateTime.fromISO(activityDate);
  const isPast = dt < DateTime.local().startOf('day');
  const location = activity.activityLocation?.placesAddress?.description;
  const grey = '#9E9E9E';
  const themeColors = (theme.color.rosters.status as any)[status] || { primary: '#F2F2F2', secondary: '#9E9E9E' };
  const actions = isPast ? [] : getRosterActions(status, hasOpenRoster);

  const handleAction = async (action: string) => {
    if (!enrolmentId) return;

    if (action === 'available' || action === 'update' || action === 'join') {
      setSessionSelectOpen(true);
      return;
    }

    if (action === 'unavailable') {
      await unavailabilityFetch.run({
        url: `${apiUrl}/vm/activity/enrolment/unavailability/save`,
        method: 'POST',
        data: { activityEnrolmentId: enrolmentId, activityDate },
      });
    }

    if (action === 'withdraw') {
      if (status === 'rostered' && hasOpenRoster && publishedRosteringId) {
        const res = await availabilityFetch.run({
          url: `${apiUrl}/vm/activity/enrolment/attendance/remove`,
          method: 'POST',
          data: { publishedRosteringId },
        });
        if (res?.ok) {
          await unavailabilityFetch.run({
            url: `${apiUrl}/vm/activity/enrolment/unavailability/save`,
            method: 'POST',
            data: { activityEnrolmentId: enrolmentId, activityDate },
          });
        }
      } else {
        await unavailabilityFetch.run({
          url: `${apiUrl}/vm/activity/enrolment/unavailability/save`,
          method: 'POST',
          data: { activityEnrolmentId: enrolmentId, activityDate },
        });
      }
    }

    if (refetch) refetch();
  };

  return (
    <>
      <Box style={{ width: 540, borderRadius: 8, border: '1px solid #efefef', overflow: 'hidden' }}>
        <Box display='flex' overflow='hidden'>
          <Box
            display='flex'
            flexDirection='column'
            alignItems='center'
            justifyContent='center'
            style={{ width: 80, minWidth: 80, background: themeColors.primary, borderRadius: '8px 0 0 8px' }}
          >
            <Typography
              style={{ fontWeight: 900, fontSize: 30, lineHeight: 1, color: themeColors.additional || '#fff' }}
            >
              {dt.day}
            </Typography>
            <Typography style={{ fontSize: 14, fontWeight: 700, color: themeColors.secondary, marginTop: 2 }}>
              {dt.monthShort}
            </Typography>
          </Box>
          <Box style={{ padding: '14px 18px', flex: 1, background: '#fff' }}>
            <Box display='flex' justifyContent='space-between' alignItems='center'>
              <Typography
                style={{ fontWeight: 800, fontSize: '1rem', color: '#545454', letterSpacing: '-0.02em', flex: 1 }}
                noWrap
              >
                {activity.name}
              </Typography>
              {!isPast && (
                <Box onClick={(e: React.MouseEvent) => e.stopPropagation()} style={{ marginLeft: 8 }}>
                  <MyActivityStatusIndicator actions={actions} status={status} onAction={handleAction} />
                </Box>
              )}
            </Box>

            <Box display='flex' flexWrap='wrap' alignItems='center' mt={0.5} style={{ gap: '8px' }}>
              {activity.startTime && activity.endTime && (
                <Box display='flex' alignItems='center'>
                  <svg
                    width='14'
                    height='14'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke={grey}
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    style={{ marginRight: 6 }}
                  >
                    <path d='M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0' />
                    <path d='M12 7v5l3 3' />
                  </svg>
                  <Typography variant='caption' style={{ color: grey }}>
                    {DateTime.fromISO(`2000-01-01T${activity.startTime}`).toFormat('h:mm a')} -{' '}
                    {DateTime.fromISO(`2000-01-01T${activity.endTime}`).toFormat('h:mm a')}
                  </Typography>
                </Box>
              )}
              {location && (
                <Box display='flex' alignItems='center'>
                  <svg
                    width='14'
                    height='14'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke={grey}
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    style={{ marginRight: 6 }}
                  >
                    <path d='M12 11m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0' />
                    <path d='M17.657 16.657l-4.243 4.243a2 2 0 0 1 -2.827 0l-4.244 -4.243a8 8 0 1 1 11.314 0z' />
                  </svg>
                  <Typography variant='caption' style={{ color: grey }} noWrap>
                    {location}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          <Box
            display='flex'
            alignItems='center'
            justifyContent='center'
            style={{
              width: 36,
              minWidth: 36,
              background: '#f5f5f5',
              borderLeft: '1px solid #efefef',
              cursor: 'pointer',
            }}
            onClick={() => {
              window.location.href = `/volunteering/activities?tab=mine&activityId=${activityId}&activityDate=${activityDate}`;
            }}
          >
            <svg
              width='20'
              height='20'
              viewBox='0 0 24 24'
              fill='none'
              stroke='#9E9E9E'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path d='M9 6l6 6l-6 6' />
            </svg>
          </Box>
        </Box>
      </Box>

      {sessionSelectOpen && (
        <ActivityAvailability
          activityId={activityId}
          selectedDate={activityDate}
          open={sessionSelectOpen}
          onClose={() => setSessionSelectOpen(false)}
          onSubmit={async (selectedDates: string[], selectedSessionIds: string[]) => {
            if (!enrolmentId) return;
            await Promise.all(
              selectedDates.map((date) =>
                availabilityFetch.run({
                  url: `${apiUrl}/vm/activity/enrolment/availability/save`,
                  method: 'POST',
                  data: {
                    activityEnrolmentId: enrolmentId,
                    activityDates: [date],
                    activityRoleIds: [],
                    sessionIds: selectedSessionIds,
                  },
                })
              )
            );
            setSessionSelectOpen(false);
            if (refetch) refetch();
          }}
        />
      )}
    </>
  );
};
